import type { QuotationDocumentData } from '@/quotation/document/types';
import QuotationHtmlDocument from '@/quotation/html/QuotationHtmlDocument';
import PdfCanvasPanel from './PdfCanvasPanel';

interface Props {
  data: QuotationDocumentData;
  mode: 'html' | 'pdf' | 'side-by-side';
}

function HtmlPanel({ data }: { data: QuotationDocumentData }) {
  return (
    <div className="flex flex-col min-h-0 h-full">
      <div className="flex items-center gap-2 px-4 py-2 bg-stone-50 border-b border-stone-200 rounded-t-xl">
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-code-s-slash-line text-ink-500 text-sm" />
        </div>
        <span className="text-xs font-semibold text-ink-600 uppercase tracking-wider">
          HTML Preview
        </span>
        <span className="ml-auto text-[10px] text-stone-400 font-mono">
          QuotationHtmlDocument
        </span>
      </div>
      <div className="overflow-y-auto flex-1 min-h-0 bg-stone-100 p-4 rounded-b-xl">
        <QuotationHtmlDocument data={data} />
      </div>
    </div>
  );
}

function PdfPanel({ data }: { data: QuotationDocumentData }) {
  return (
    <div className="flex flex-col min-h-0 h-full">
      <div className="flex items-center gap-2 px-4 py-2 bg-stone-50 border-b border-stone-200 rounded-t-xl">
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-file-pdf-line text-red-500 text-sm" />
        </div>
        <span className="text-xs font-semibold text-ink-600 uppercase tracking-wider">
          Actual PDF Preview
        </span>
        <span className="ml-auto text-[10px] text-stone-400 font-mono">
          QuotationPdfDocument
        </span>
      </div>
      <div className="flex-1 min-h-0 rounded-b-xl overflow-hidden">
        <PdfCanvasPanel
          key={`${data.quotationNumber}-${data.documentLocale}-${data.documentType}`}
          data={data}
        />
      </div>
    </div>
  );
}

export default function StudioPreview({ data, mode }: Props) {
  if (mode === 'html') {
    return (
      <div className="rounded-xl border border-stone-200 overflow-hidden" style={{ minHeight: 700 }}>
        <HtmlPanel data={data} />
      </div>
    );
  }

  if (mode === 'pdf') {
    return (
      <div className="rounded-xl border border-stone-200 overflow-hidden" style={{ minHeight: 700 }}>
        <PdfPanel data={data} />
      </div>
    );
  }

  // Side by side
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" style={{ minHeight: 700 }}>
      <div className="rounded-xl border border-stone-200 overflow-hidden flex flex-col">
        <HtmlPanel data={data} />
      </div>
      <div className="rounded-xl border border-stone-200 overflow-hidden flex flex-col">
        <PdfPanel data={data} />
      </div>
    </div>
  );
}